import axios from 'axios';

// The workspace's employee privacy & AI notice (PDR 0024), read the SAME way
// on the login page's silent probe and on the public route it links to
// (issue 576). Both callers need the UNAUTHENTICATED route
// `GET /api/privacy/public/employee-notice` — never `privacyApi` (utils/api.ts),
// which is a bearer+X-Tenant-Slug client for the SIGNED-IN editor surface and
// would attach headers this route neither needs nor is guarded by.
//
// Same-origin, no runtime base-url key — same reasoning `privacyApi` itself
// documents: the tenant host already routes `/api/privacy` to privacy-api
// (architecture.manifest.json), so a bare instance pointed at this origin is
// both correct and unconfigurable, on a bare/dev host too (the request is
// resolved by `tenantSlug`, not by the host).
const publicPrivacyApi = axios.create({
  baseURL: typeof window !== 'undefined' ? `${window.location.origin}/api/privacy` : '/api/privacy',
  timeout: 8000,
});

export type PublicEmployeeNotice = {
  locale: string;
  title: string;
  contentMd: string;
  // null for a workspace with no notice rows of its own yet — served Lumi's
  // template read-only, which was never "updated" by this tenant.
  updatedAt: string | null;
  lumiSourceVersion: number | null;
};

/**
 * `locale` is a PREFERENCE, not a guarantee — the route itself falls back
 * requested → en → de server-side and answers with whichever locale it found
 * in `data.locale`, so a caller renders that field rather than assuming its
 * own ask was honoured. Throws (404 `EMPLOYEE_NOTICE_NOT_FOUND`, or a network
 * failure) when no locale at all is published; every caller here treats that
 * as "say nothing" rather than an error to surface.
 */
export async function fetchPublicEmployeeNotice(
  tenantSlug: string,
  locale: 'de' | 'en',
): Promise<PublicEmployeeNotice> {
  const { data } = await publicPrivacyApi.get('/public/employee-notice', {
    params: { tenantSlug, locale },
  });
  return data as PublicEmployeeNotice;
}
